import {
  Button as AntButton,
  ButtonProps as AntButtonProps,
  theme,
} from 'antd';
import styled, { css } from 'styled-components';
import { ReactNode } from 'react';
import type { GlobalToken } from 'antd';
import { radius } from '../../theme/radius';

export type IconButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'utility'
  | 'danger';
export type IconButtonSize = 'xs' | 's' | 'm' | 'l';
export type IconButtonShape = 'square' | 'round';

export interface IconButtonProps extends Omit<
  AntButtonProps,
  'type' | 'size' | 'variant' | 'icon' | 'shape'
> {
  /** Button variant following Figma design system */
  variant?: IconButtonVariant;
  /** Button size; `xs` is for inline use next to text */
  size?: IconButtonSize;
  /** Button shape - square (default) or round (circular) */
  shape?: IconButtonShape;
  /** Icon to display */
  icon: ReactNode;
  /** Icon size in px, overriding the size preset's */
  iconSize?: number;
}

const sizeConfig = {
  xs: { size: 14, padding: 0, borderRadius: 4, iconSize: 14 },
  s: { size: 32, padding: 8, borderRadius: 6, iconSize: 16 },
  m: { size: 40, padding: 8, borderRadius: 8, iconSize: 24 },
  l: { size: 56, padding: 12, borderRadius: 8, iconSize: 32 },
} as const;

const getVariantStyles = (token: GlobalToken, variant: IconButtonVariant) => {
  const variants = {
    primary: css`
      background: ${token.colorPrimary};
      color: ${token.colorWhite};

      &:hover:not(:disabled) {
        background: ${token.colorPrimaryHover} !important;
        color: ${token.colorWhite} !important;
      }

      &:active:not(:disabled) {
        background: ${token.colorPrimaryActive} !important;
        color: ${token.colorWhite} !important;
      }

      &:disabled {
        background: ${token.colorBgContainerDisabled};
        color: ${token.colorTextDisabled};
      }
    `,
    secondary: css`
      background: ${token.colorText};
      color: ${token.colorWhite};

      &:hover:not(:disabled) {
        background: ${token.colorTextSecondary} !important;
        color: ${token.colorWhite} !important;
      }

      &:active:not(:disabled) {
        background: ${token.colorTextBase} !important;
        color: ${token.colorWhite} !important;
      }

      &:disabled {
        background: ${token.colorBgContainerDisabled};
        color: ${token.colorTextDisabled};
      }
    `,
    tertiary: css`
      background: transparent;
      color: ${token.colorTextSecondary};
      box-shadow: none;

      &:hover:not(:disabled) {
        background: transparent !important;
        color: ${token.colorTextTertiary} !important;
      }

      &:active:not(:disabled) {
        background: transparent !important;
        color: ${token.colorText} !important;
      }

      &:disabled {
        background: transparent;
        color: ${token.colorTextDisabled};
      }
    `,
    utility: css`
      background: ${token.colorBgLayout};
      color: ${token.colorTextSecondary};

      &:hover:not(:disabled) {
        background: ${token.colorBorder} !important;
        color: ${token.colorTextSecondary} !important;
      }

      &:active:not(:disabled) {
        background: ${token.colorPrimaryBg} !important;
        color: ${token.colorTextSecondary} !important;
      }

      &:disabled {
        background: ${token.colorBgContainerDisabled};
        color: ${token.colorTextDisabled};
      }
    `,
    danger: css`
      background: transparent;
      color: ${token.colorTextTertiary};
      box-shadow: none;

      &:hover:not(:disabled) {
        background: ${token.colorErrorBg} !important;
        color: ${token.colorError} !important;
      }

      &:active:not(:disabled) {
        background: ${token.colorErrorBg} !important;
        color: ${token.colorError} !important;
      }

      &:disabled {
        background: transparent;
        color: ${token.colorTextDisabled};
      }
    `,
  };
  return variants[variant];
};

interface StyledIconButtonProps {
  $token: GlobalToken;
  $variant: IconButtonVariant;
  $size: IconButtonSize;
  $shape: IconButtonShape;
  $iconSize?: number;
}

const StyledIconButton = styled(AntButton)<StyledIconButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: all ${({ $token }) => $token.motionDurationMid} ease;

  /* Size */
  width: ${({ $size }) => sizeConfig[$size].size}px;
  height: ${({ $size }) => sizeConfig[$size].size}px;
  min-width: ${({ $size }) => sizeConfig[$size].size}px;
  padding: ${({ $size }) => sizeConfig[$size].padding}px;
  border-radius: ${({ $size, $shape }) =>
    $shape === 'round' ? radius.circle : `${sizeConfig[$size].borderRadius}px`};

  /* Variant styles */
  ${({ $token, $variant }) => getVariantStyles($token, $variant)}

  &:disabled {
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
  }

  /* Keyboard focus only, mouse clicks stay outline-free */
  &:focus-visible {
    outline: 2px solid ${({ $token }) => $token.colorPrimary};
    outline-offset: 2px;
  }

  /* Icon sizing */
  svg {
    width: ${({ $size, $iconSize }) => $iconSize ?? sizeConfig[$size].iconSize}px;
    height: ${({ $size, $iconSize }) => $iconSize ?? sizeConfig[$size].iconSize}px;
  }
`;

/**
 * IconButton component based on Figma Design System
 *
 * A square button that contains only an icon.
 * Now uses Ant Design theme tokens for consistent styling.
 *
 * @example
 * ```tsx
 * import { IconButton } from '@libertify/ui';
 * import { PlusOutlined } from '@ant-design/icons';
 *
 * <IconButton icon={<PlusOutlined />} variant="primary" size="m" />
 * <IconButton icon={<SettingOutlined />} variant="utility" size="l" />
 * <IconButton icon={<SearchOutlined />} variant="tertiary" size="s" shape="round" />
 * ```
 */
export const IconButton = ({
  variant = 'primary',
  size = 'm',
  shape = 'square',
  icon,
  iconSize,
  ...props
}: IconButtonProps) => {
  const { token } = theme.useToken();

  return (
    <StyledIconButton
      $token={token}
      $variant={variant}
      $size={size}
      $shape={shape}
      $iconSize={iconSize}
      {...props}
    >
      {icon}
    </StyledIconButton>
  );
};

export default IconButton;
